import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../services/supabase';
import { 
    RefreshCw, Search, List, LayoutGrid, Send, Download, X, 
    Printer, Users, AlertCircle, Clock, CheckSquare, Square,
    CalendarDays, FileText, Filter, XCircle, FilterX, Activity,
    ArrowUpDown, Phone, Calendar, Mail, User, ChevronLeft, ChevronRight, ArrowLeft,
    FileSpreadsheet, FileJson, FileType, Paperclip, ArrowRight, BarChart3
} from 'lucide-react';

import ClientTableView from './ClientTableView';
import ClientKanbanView from './ClientKanbanView';
import ClientDetailModal from '../modals/ClientDetailModal';
import EmailComposeModal from '../modals/EmailComposeModal';
import DownloadModal from '../modals/DownloadModal';
import PrintModal from '../modals/PrintModal';
import ContactsListModal from '../modals/ContactsListModal';
import NotesListModal from '../modals/NotesListModal';
import AddCompanyModal from '../modals/AddCompanyModal';
import EnrolClientModal from '../modals/EnrolClientModal';
import HmrcSyncHistoryModal from '../modals/HmrcSyncHistoryModal';
import TokenExpiryWarning from './TokenExpiryWarning';
import BulkAssignModal from '../modals/BulkAssignModal';
import BulkStatusModal from '../modals/BulkStatusModal';
import BulkServicesModal from '../modals/BulkServicesModal';
import { LoadingSkeleton, Card, Button, Badge } from '../ui/UIComponents';
import FilterPresets from '../ui/FilterPresets';
import WorkstreamKpiStrip from './WorkstreamKpiStrip';
import {
    buildWorkstreamState,
    getMonthOptions,
    getModuleEntity,
    getModuleWorkstreams,
    getWorkstreamByKey,
    getWorkstreamColorClasses
} from '../../config/moduleHelpers';

/**
 * GenericClientModule - Reusable client management module
 * 
 * Expects config object from moduleDefs.js with:
 * - tableName: Database table name
 * - columns: Array of column definitions
 * - workflowTables: { acc, tax, cs } - Status workflow table names
 * - fieldMappings: { searchFields, dueFields, statusFields, staffFields, planFields }
 * - validation: { numberMinLength, requiredFields }
 * - newRecordDefaults: Default values for new records
 */

// --- TOOLTIPS (from old App.jsx) ---
const PortalTooltip = ({ client = {}, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [coords, setCoords] = useState({ left: 0, top: 0 });
    const triggerRef = useRef(null);

    const handleMouseEnter = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setCoords({ left: rect.right + 10, top: rect.top + (rect.height / 2) });
            setIsVisible(true);
        }
    };

    const hasContactInfo = client.pc_fname || client.pc_email || (client.pc_tel && client.pc_tel !== '00000000000');
    const hasNotes = client.notes && client.notes.trim().length > 0;

    if (!hasContactInfo && !hasNotes) return <div className="inline-block">{children}</div>;

    return (
        <>
            <div ref={triggerRef} onMouseEnter={handleMouseEnter} onMouseLeave={() => setIsVisible(false)} className="inline-block">
                {children}
            </div>
            {isVisible && createPortal(
                <div 
                  className="fixed z-[9999] bg-slate-800 text-white p-3 rounded-lg shadow-xl border border-slate-700 text-xs w-64 pointer-events-none"
                  style={{ left: `${coords.left}px`, top: `${coords.top}px`, transform: 'translateY(-50%)' }}
                >
                  <div className="font-bold text-slate-100 border-b border-slate-600 pb-1 mb-2">Primary Contact</div>
                  {client.pc_fname && <div className="mb-1.5 flex items-center gap-2"><User className="w-3.5 h-3.5" /><span>{client.pc_fname}</span></div>}
                  {client.pc_email && <div className="mb-1.5 flex items-center gap-2"><Mail className="w-3.5 h-3.5" /><span>{client.pc_email}</span></div>}
                  {client.pc_tel && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /><span>{client.pc_tel}</span></div>}
                  {hasNotes && <div className="mt-2 pt-2 border-t border-slate-600"><FileText className="w-3.5 h-3.5 inline mr-2" />{client.notes}</div>}
                </div>,
                document.body
            )}
        </>
    );
};

const NotesTooltip = ({ notes = '', children }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [coords, setCoords] = useState({ left: 0, top: 0 });
    const triggerRef = useRef(null);

    const handleMouseEnter = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setCoords({ left: rect.right + 10, top: rect.top + (rect.height / 2) });
            setIsVisible(true);
        }
    };

    if (!notes) return children;

    return (
        <>
            <div ref={triggerRef} onMouseEnter={handleMouseEnter} onMouseLeave={() => setIsVisible(false)} className="inline-block cursor-help">
                {children}
            </div>
            {isVisible && createPortal(
                <div 
                  className="fixed z-[9999] bg-slate-800 text-white p-3 rounded-lg shadow-xl border border-slate-700 text-xs w-64 pointer-events-none"
                  style={{ left: `${coords.left}px`, top: `${coords.top}px`, transform: 'translateY(-50%)' }}
                >
                  <div className="font-bold text-amber-400 border-b border-slate-600 pb-1 mb-2">Notes</div>
                  <div className="text-slate-300 italic">{notes}</div>
                </div>,
                document.body
            )}
        </>
    );
};

// --- MAIN MODULE ---
const GenericClientModule = ({ config, staffList, dashboardDrilldown = null, onReturnToDrilldownSource = null }) => {
    console.log('GenericClientModule mounting:', config?.id, config);
    const entity = useMemo(() => getModuleEntity(config), [config]);
    const workstreams = useMemo(() => getModuleWorkstreams(config), [config]);
    const dueWorkstreams = useMemo(() => workstreams.filter(workstream => workstream.dueField), [workstreams]);
    const planWorkstreams = useMemo(() => workstreams.filter(workstream => workstream.planField), [workstreams]);
    const statusWorkstreams = useMemo(() => workstreams.filter(workstream => workstream.statusField), [workstreams]);
    const primaryWorkstreamKey = workstreams[0]?.key || 'general';
    const planningMonths = useMemo(() => getMonthOptions(24), []);
    const [clients, setClients] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // View & Filtering
    const [viewMode, setViewMode] = useState('list');
    const [plannerType, setPlannerType] = useState(primaryWorkstreamKey);
    const [showPlannerDropdown, setShowPlannerDropdown] = useState(false);
    const plannerButtonRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState({ category: 'all', type: 'all' });
    const [dateFilter, setDateFilter] = useState({ type: 'all', start: '', end: '' });
    const [workStatusFilter, setWorkStatusFilter] = useState({});
    const [planFilters, setPlanFilters] = useState({});
    const [filterNotes, setFilterNotes] = useState('all');
    const [filterStaff, setFilterStaff] = useState('all');
    
    // Sorting
    const [sortColumn, setSortColumn] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    
    // Selection & Modals
    const [isBulkEditMode, setIsBulkEditMode] = useState(false);
    const [selectedClients, setSelectedClients] = useState(new Set());
    const [selectedClientForModal, setSelectedClientForModal] = useState(null);
    const [showContactsTable, setShowContactsTable] = useState(false);
    const [showNotesTable, setShowNotesTable] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [showMoveDropdown, setShowMoveDropdown] = useState(false);
    const [showHmrcSyncHistory, setShowHmrcSyncHistory] = useState(false);
    const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
    const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
    const [showBulkServicesModal, setShowBulkServicesModal] = useState(false);
    const [emailRecipients, setEmailRecipients] = useState([]);
    const [selectedClientData, setSelectedClientData] = useState([]);
    const [showAddClientModal, setShowAddClientModal] = useState(false);
    const [isSyncingExternal, setIsSyncingExternal] = useState(false);
    const [isConnectingHmrc, setIsConnectingHmrc] = useState(false);
    const [hmrcEnvStatus, setHmrcEnvStatus] = useState(null);
    const [isRequestingAuth, setIsRequestingAuth] = useState(false);
    const [isCheckingInvite, setIsCheckingInvite] = useState(false);
    const hmrcCallbackHandledRef = useRef(false);
    const realtimeRefreshTimerRef = useRef(null);
    const lastDashboardDrilldownNonceRef = useRef(null);
    
    // HMRC Connection Health
    const [hmrcHealthStats, setHmrcHealthStats] = useState({ connected: 0, notAuthorised: 0, needsReconnecting: 0 });
    
    // Filter Presets
    const [activePreset, setActivePreset] = useState(null);
    const [dashboardContextChip, setDashboardContextChip] = useState(null);
    const [kpiStageFilter, setKpiStageFilter] = useState('all');
    const [kpiDueMonthFilter, setKpiDueMonthFilter] = useState('all');
    const [kpiStaffFilter, setKpiStaffFilter] = useState('all');
    
    const getDefaultMtdPeriodKey = () => {
        const month = new Date().getMonth() + 1;
        if (month >= 4 && month <= 6) return 'q1';
        if (month >= 7 && month <= 9) return 'q2';
        if (month >= 10 && month <= 12) return 'q3';
        return 'q4';
    };
    const [selectedKpiWorkstreamKey, setSelectedKpiWorkstreamKey] = useState(() => (
        config?.id === 'mtd_itsa'
            ? getDefaultMtdPeriodKey()
            : (dueWorkstreams[0]?.key || primaryWorkstreamKey)
    ));

    const getMonthKey = (dateValue) => {
        if (!dateValue) return null;
        const parsed = new Date(dateValue);
        if (!Number.isNaN(parsed.getTime())) {
            return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
        }

        const raw = String(dateValue);
        const match = raw.match(/^(\d{4})-(\d{2})/);
        return match ? `${match[1]}-${match[2]}` : null;
    };
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
    
    // Update sortConfig when sortColumn or sortDirection changes
    useEffect(() => {
        setSortConfig({ key: sortColumn, direction: sortDirection });
    }, [sortColumn, sortDirection]);

    // Workflows - fetched from database lookup tables
    const [workflows, setWorkflows] = useState({});

    const supportsBulkServiceEdit = useMemo(
        () => ['ch_company', 'charity', 'individual', 'unregistered', 'partnership'].includes(config?.id),
        [config?.id]
    );

    useEffect(() => {
        if (!isBulkEditMode) {
            setSelectedClients(new Set());
        }
    }, [isBulkEditMode]);

    const activePlannerWorkstream = useMemo(
        () => getWorkstreamByKey(config, plannerType) || planWorkstreams[0] || workstreams[0] || null,
        [config, plannerType, planWorkstreams, workstreams]
    );

    useEffect(() => {
        setPlannerType(primaryWorkstreamKey);
        setWorkStatusFilter(buildWorkstreamState(config, 'all', workstream => Boolean(workstream.statusField)));
        setPlanFilters(buildWorkstreamState(config, 'all', workstream => Boolean(workstream.planField)));
        setStatusFilter({ category: 'all', type: 'all' });
        setDateFilter({ type: 'all', start: '', end: '' });
        setFilterNotes('all');
        setFilterStaff('all');
        setSearchTerm('');
        setSelectedKpiWorkstreamKey(
            config?.id === 'mtd_itsa'
                ? getDefaultMtdPeriodKey()
                : (dueWorkstreams[0]?.key || primaryWorkstreamKey)
        );
        setKpiStageFilter('all');
        setKpiDueMonthFilter('all');
        setKpiStaffFilter('all');
    }, [config, primaryWorkstreamKey, dueWorkstreams]);

    useEffect(() => {
        if (config?.id !== 'individual') return;
        if (hmrcCallbackHandledRef.current) return;

        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');
        const state = url.searchParams.get('state');

        if (!code || !state) return;

        hmrcCallbackHandledRef.current = true;

        const handleHmrcCallback = async () => {
            try {
                let clientId = null;
                let callbackMode = 'callback';
                try {
                    const decodedState = JSON.parse(atob(state));
                    if (decodedState?.mode === 'agent') {
                        callbackMode = 'callback_agent';
                    }
                    clientId = Number(decodedState?.clientId);
                } catch {
                    throw new Error('Unable to parse HMRC callback state.');
                }

                if (callbackMode === 'callback' && (!clientId || Number.isNaN(clientId))) {
                    throw new Error('HMRC callback missing valid client reference.');
                }

                const { data, error } = await supabase.functions.invoke('hmrc-auth-connect', {
                    body: {
                        mode: callbackMode,
                        clientId: callbackMode === 'callback' ? clientId : undefined,
                        code,
                    },
                });

                if (error) throw error;

                if (!data?.success) {
                    throw new Error(data?.error || 'HMRC callback did not complete successfully.');
                }

                alert(callbackMode === 'callback_agent'
                    ? 'HMRC agent connection completed successfully.'
                    : 'HMRC connection completed successfully.');
                await fetchData();
            } catch (err) {
                const detail = err?.message || 'Unknown HMRC callback error';
                console.error('HMRC callback handling error:', err);
                alert(`HMRC callback failed: ${detail}`);
            } finally {
                url.searchParams.delete('code');
                url.searchParams.delete('state');
                window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
            }
        };

        handleHmrcCallback();
    }, [config?.id]);

    useEffect(() => {
        if (config?.id !== 'individual') return;

        const fetchHmrcEnvStatus = async () => {
            try {
                const { data, error } = await supabase.functions.invoke('hmrc-auth-connect', {
                    body: { mode: 'env_status' },
                });

                if (error) throw error;
                setHmrcEnvStatus(data || null);
            } catch (err) {
                console.warn('Unable to fetch HMRC environment status:', err?.message || err);
                setHmrcEnvStatus({
                    hmrcEnvironment: 'unknown',
                    isMixedEnvironment: false,
                });
            }
        };

        fetchHmrcEnvStatus();
    }, [config?.id]);

    // Fetch Data
    useEffect(() => {
        if (config?.tableName) {
            fetchData();
            fetchStatusOptions();
        }
    }, [config]);

    // Fetch status options from lookup tables
    const fetchStatusOptions = async () => {
        try {
            const workflowResults = await Promise.all(
                statusWorkstreams.map(async workstream => {
                    const tableName = workstream.workflowTable || config.workflowTables?.[workstream.key];
                    if (!tableName) {
                        return [workstream.key, []];
                    }

                    const response = await supabase
                        .from(tableName)
                        .select('status_name')
                        .order('step_order', { ascending: true });

                    const statuses = response.data
                        ?.map(item => item.status_name)
                        .filter(status => status && status !== '--SET STATUS--') || [];

                    return [workstream.key, statuses];
                })
            );

            setWorkflows(Object.fromEntries(workflowResults));
        } catch (error) {
            console.error('Error fetching status options:', error);
            setWorkflows(Object.fromEntries(statusWorkstreams.map(workstream => [workstream.key, []])));
        }
    };

    const fetchData = async () => {
        setIsLoading(true);
        const { data, error } = await supabase
            .from(config.fetchTable || config.tableName)
            .select('*');
        
        if (error) console.error('Error fetching:', error);
        if (data) {
            const scopedData = typeof config.postFetchFilter === 'function'
                ? data.filter(record => {
                    try {
                        return config.postFetchFilter(record);
                    } catch {
                        return true;
                    }
                })
                : data;

            const mainField = config.fieldMappings?.mainField || 'name';
            const numberField = config.fieldMappings?.numberField || 'number';

            const toIsoDate = (date) => {
                if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
                return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString().split('T')[0];
            };

            const parseTaxYearEnd = (record) => {
                // Preferred: explicit tax year end date
                if (record?.sa_tax_year_end) {
                    const parsed = new Date(record.sa_tax_year_end);
                    if (!Number.isNaN(parsed.getTime())) return parsed;
                }

                // Fallback: HMRC next tax year format e.g. "2026-27" => tax year ending 05/04/2027
                const nextTaxYear = String(record?.hmrc_next_tax_year || '');
                const match = nextTaxYear.match(/(\d{4})\s*[-/]\s*(\d{2,4})/);
                if (match) {
                    const startYear = Number(match[1]);
                    const endPart = Number(match[2]);
                    const endYear = endPart < 100 ? Math.floor(startYear / 100) * 100 + endPart : endPart;
                    return new Date(Date.UTC(endYear, 3, 5)); // 05 April end of UK tax year
                }

                // Last fallback: infer from SA due date (31 Jan following tax year end)
                if (record?.sa_due_date) {
                    const saDue = new Date(record.sa_due_date);
                    if (!Number.isNaN(saDue.getTime())) {
                        return new Date(Date.UTC(saDue.getUTCFullYear() - 1, 3, 5));
                    }
                }

                return null;
            };

            const buildMtdSchedule = (record) => {
                const taxYearEnd = parseTaxYearEnd(record);
                if (!taxYearEnd) {
                    return {
                        mtd_q1_due: null,
                        mtd_q2_due: null,
                        mtd_q3_due: null,
                        mtd_q4_due: null,
                        mtd_q1_due_date: null,
                        mtd_q2_due_date: null,
                        mtd_q3_due_date: null,
                        mtd_q4_due_date: null,
                        mtd_final_declaration_due: null,
                        mtd_next_obligation_label: null,
                        mtd_next_obligation_due: null,
                        mtd_obligations_overdue: 0,
                        mtd_overdue_obligations: 0,
                    };
                }

                const endYear = taxYearEnd.getUTCFullYear();
                const obligations = [
                    { key: 'Q1', due: new Date(Date.UTC(endYear - 1, 7, 7)) }, // 07 Aug
                    { key: 'Q2', due: new Date(Date.UTC(endYear - 1, 10, 7)) }, // 07 Nov
                    { key: 'Q3', due: new Date(Date.UTC(endYear, 1, 7)) }, // 07 Feb
                    { key: 'Q4', due: new Date(Date.UTC(endYear, 4, 7)) }, // 07 May
                    { key: 'Final Declaration', due: new Date(Date.UTC(endYear + 1, 0, 31)) }, // 31 Jan following year
                ];

                const today = new Date();
                const todayUtc = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));

                const nextObligation = obligations.find(item => item.due >= todayUtc) || obligations[obligations.length - 1];
                const overdueCount = obligations.filter(item => item.due < todayUtc).length;

                const q1Due = record?.mtd_q1_due_date || toIsoDate(obligations[0].due);
                const q2Due = record?.mtd_q2_due_date || toIsoDate(obligations[1].due);
                const q3Due = record?.mtd_q3_due_date || toIsoDate(obligations[2].due);
                const q4Due = record?.mtd_q4_due_date || toIsoDate(obligations[3].due);
                const finalDue = record?.mtd_final_declaration_due || toIsoDate(obligations[4].due);
                const nextLabel = record?.mtd_next_obligation_label || nextObligation.key;
                const nextDue = record?.mtd_next_obligation_due || toIsoDate(nextObligation.due);
                const overdueValue = Number.isFinite(Number(record?.mtd_obligations_overdue))
                    ? Number(record.mtd_obligations_overdue)
                    : overdueCount;

                return {
                    // Legacy aliases retained for compatibility
                    mtd_q1_due: q1Due,
                    mtd_q2_due: q2Due,
                    mtd_q3_due: q3Due,
                    mtd_q4_due: q4Due,

                    // Phase 2.1 canonical fields
                    mtd_q1_due_date: q1Due,
                    mtd_q2_due_date: q2Due,
                    mtd_q3_due_date: q3Due,
                    mtd_q4_due_date: q4Due,
                    mtd_final_declaration_due: finalDue,
                    mtd_next_obligation_label: nextLabel,
                    mtd_next_obligation_due: nextDue,
                    mtd_obligations_overdue: overdueValue,
                    mtd_overdue_obligations: overdueValue,
                };
            };

            const buildSyncHealth = (record) => {
                const hasError = record.hmrc_last_error && String(record.hmrc_last_error).trim();
                if (hasError) return 'failed';
                const lastSync = record.hmrc_last_sync_at;
                if (!lastSync) return 'never';
                const diffHours = (Date.now() - new Date(lastSync).getTime()) / (1000 * 60 * 60);
                if (diffHours < 24) return 'fresh';
                if (diffHours < 168) return 'stale';
                return 'old';
            };

            const formatted = scopedData.map(record => ({
                ...record,
                ...(config?.id === 'mtd_itsa' ? buildMtdSchedule(record) : {}),
                hmrc_sync_health: buildSyncHealth(record),
                id: record.id,
                name: record[mainField] || record.name || '',
                number: record[numberField] || record.number || ''
            }));
            setClients(formatted);
        }
        setIsLoading(false);
    };

    // Lightweight realtime sync for relational enrolment modules.
    // Refreshes when source clients or enrolment records change.
    useEffect(() => {
        if (!config?.enrollMode) return;

        const realtimeTables = config?.realtimeTables?.length
            ? config.realtimeTables
            : ['clients_books', 'clients_coy', 'clients_charity', 'clients_individual'];

        const scheduleRefresh = () => {
            if (realtimeRefreshTimerRef.current) {
                clearTimeout(realtimeRefreshTimerRef.current);
            }
            realtimeRefreshTimerRef.current = setTimeout(() => {
                fetchData();
            }, 250);
        };

        let channel = supabase.channel(`${config?.id || 'relational'}-sync-${Date.now()}`);
        realtimeTables.forEach(table => {
            channel = channel.on(
                'postgres_changes',
                { event: '*', schema: 'public', table },
                scheduleRefresh
            );
        });
        channel = channel.subscribe();

        return () => {
            if (realtimeRefreshTimerRef.current) {
                clearTimeout(realtimeRefreshTimerRef.current);
                realtimeRefreshTimerRef.current = null;
            }
            supabase.removeChannel(channel);
        };
    }, [config]);

    // Helper Functions
    const getDaysRemaining = (dateString) => {
        if (!dateString) return 999;
        const today = new Date();
        const due = new Date(dateString);
        return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    };

    // Stats Calculation
    const stats = useMemo(() => {
        const createCounter = () => ({
            total: 0,
            byWorkstream: Object.fromEntries(dueWorkstreams.map(workstream => [workstream.key, 0]))
        });

        const overdue = createCounter();
        const due14 = createCounter();
        const dueSoon = createCounter();
        
        let hmrcConnected = 0;
        let hmrcNotAuthorised = 0;
        let hmrcNeedsReconnecting = 0;
        
        clients.forEach(client => {
            const workstreamDays = dueWorkstreams.map(workstream => ({
                key: workstream.key,
                days: getDaysRemaining(client[workstream.dueField])
            }));

            const overdueKeys = workstreamDays.filter(item => item.days < 0).map(item => item.key);
            const due14Keys = workstreamDays.filter(item => item.days >= 0 && item.days < 14).map(item => item.key);
            const dueSoonKeys = workstreamDays.filter(item => item.days >= 0 && item.days < 30).map(item => item.key);

            if (overdueKeys.length > 0) {
                overdue.total++;
                overdueKeys.forEach(key => {
                    overdue.byWorkstream[key] = (overdue.byWorkstream[key] || 0) + 1;
                });
            }

            if (due14Keys.length > 0) {
                due14.total++;
                due14Keys.forEach(key => {
                    due14.byWorkstream[key] = (due14.byWorkstream[key] || 0) + 1;
                });
            }

            if (dueSoonKeys.length > 0) {
                dueSoon.total++;
                dueSoonKeys.forEach(key => {
                    dueSoon.byWorkstream[key] = (dueSoon.byWorkstream[key] || 0) + 1;
                });
            }
            
            // HMRC connection health (Individual module only)
            if (config?.id === 'individual') {
                const agentStatus = client.hmrc_agent_auth_status || 'unknown';
                const tokenStatus = client.hmrc_auth_status || 'not_connected';
                
                if (agentStatus === 'authorised' && tokenStatus === 'connected') {
                    hmrcConnected++;
                } else if (agentStatus === 'not_authorised' || tokenStatus === 'expired' || tokenStatus === 'revoked') {
                    hmrcNotAuthorised++;
                } else if (agentStatus === 'unknown' || tokenStatus === 'not_connected') {
                    hmrcNeedsReconnecting++;
                }
            }
        });

        if (config?.id === 'individual') {
            setHmrcHealthStats({
                connected: hmrcConnected,
                notAuthorised: hmrcNotAuthorised,
                needsReconnecting: hmrcNeedsReconnecting
            });
        }

        return { total: clients.length, overdue, due14, dueSoon };
    }, [clients, dueWorkstreams, config?.id]);

    const selectedKpiWorkstream = useMemo(() => {
        return getWorkstreamByKey(config, selectedKpiWorkstreamKey) || dueWorkstreams[0] || null;
    }, [config, selectedKpiWorkstreamKey, dueWorkstreams]);

    const workstreamKpiStats = useMemo(() => {
        if (!selectedKpiWorkstream?.statusField || !selectedKpiWorkstream?.dueField) return null;

        const scopedClients = kpiDueMonthFilter === 'all'
            ? clients
            : clients.filter(client => getMonthKey(client[selectedKpiWorkstream.dueField]) === kpiDueMonthFilter);

        let overdue = 0;
        let notStarted = 0;
        let inProgress = 0;
        let completed = 0;
        const staffLoad = {};

        scopedClients.forEach(client => {
            const days = getDaysRemaining(client[selectedKpiWorkstream.dueField]);
            if (days < 0) overdue++;

            const statusRaw = client[selectedKpiWorkstream.statusField];
            const status = String(statusRaw || '').toLowerCase().trim();

            const isNotStarted = !status || status.includes('not started') || status.includes('not_started') || status === 'new';
            const isCompleted = status.includes('complet') || status.includes('filed') || status.includes('submit') || status.includes('approved') || status.includes('done');

            if (isNotStarted) {
                notStarted++;
            } else if (isCompleted) {
                completed++;
            } else {
                inProgress++;
            }

            const staffId = selectedKpiWorkstream.staffField ? client[selectedKpiWorkstream.staffField] : null;
            if (staffId) {
                staffLoad[staffId] = (staffLoad[staffId] || 0) + 1;
            }
        });

        const total = scopedClients.length;
        const pctNotStarted  = total > 0 ? Math.round((notStarted / total) * 100) : 0;
        const pctInProgress  = total > 0 ? Math.round((inProgress  / total) * 100) : 0;
        const pctDone        = total > 0 ? Math.round((completed   / total) * 100) : 0;
        const pctOverdue     = total > 0 ? Math.round((overdue     / total) * 100) : 0;
        const staffWorkload = Object.entries(staffLoad)
            .map(([id, count]) => {
                const member = staffList.find(s => String(s.id) === String(id));
                return { id, name: member?.name || `Staff ${id}`, count };
            })
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);

        return {
            key: selectedKpiWorkstream.key,
            label: selectedKpiWorkstream.label,
            total,
            overdue,
            notStarted,
            inProgress,
            completed,
            pctNotStarted,
            pctInProgress,
            pctDone,
            pctOverdue,
            staffWorkload
        };
    }, [clients, kpiDueMonthFilter, selectedKpiWorkstream, staffList]);

    const kpiDueMonthOptions = useMemo(() => {
        if (!selectedKpiWorkstream?.dueField) return [];

        const uniqueMonths = new Set();
        clients.forEach(client => {
            const monthKey = getMonthKey(client[selectedKpiWorkstream.dueField]);
            if (monthKey) uniqueMonths.add(monthKey);
        });

        return Array.from(uniqueMonths).sort((a, b) => a.localeCompare(b));
    }, [clients, selectedKpiWorkstream]);

    // Filtering Logic
    const filteredClients = useMemo(() => {
        return clients.filter(client => {
            const searchLower = searchTerm.toLowerCase();
            const searchFields = config.fieldMappings?.searchFields || [config.fieldMappings?.mainField || 'name', config.fieldMappings?.numberField || 'number'];
            const matchesSearch = searchFields.some(field => {
                const value = client[field];
                return value && value.toString().toLowerCase().includes(searchLower);
            });
            
            // Staff filter using config
            const staffFields = statusWorkstreams.map(workstream => workstream.staffField).filter(Boolean);
            const matchesStaff = filterStaff === 'all'
                ? true
                : filterStaff === 'unassigned'
                    ? !staffFields.some(field => Boolean(client[field]))
                    : staffFields.some(field => String(client[field] ?? '') === String(filterStaff));
            
            // Status Filter using config due date fields
            let matchesStatus = true;
            const workstreamDays = dueWorkstreams.map(workstream => ({
                key: workstream.key,
                days: getDaysRemaining(client[workstream.dueField])
            }));

            if (statusFilter.category !== 'all') {
                if (statusFilter.category === 'ontrack') {
                    if (statusFilter.type === 'all') {
                        matchesStatus = workstreamDays.every(item => item.days >= 0);
                    } else {
                        const selectedWorkstream = workstreamDays.find(item => item.key === statusFilter.type);
                        matchesStatus = selectedWorkstream ? selectedWorkstream.days >= 0 : false;
                    }
                } else {
                    const matcher = statusFilter.category === 'overdue'
                        ? (days) => days < 0
                        : statusFilter.category === 'due14'
                            ? (days) => days >= 0 && days < 14
                            : (days) => days >= 0 && days < 30;

                    if (statusFilter.type === 'all') {
                        matchesStatus = workstreamDays.some(item => matcher(item.days));
                    } else {
                        const selectedWorkstream = workstreamDays.find(item => item.key === statusFilter.type);
                        matchesStatus = selectedWorkstream ? matcher(selectedWorkstream.days) : false;
                    }
                }
            }

            // Date Filter
            let matchesDate = true;
            if (dateFilter.start || dateFilter.end) {
                const start = dateFilter.start ? new Date(dateFilter.start) : new Date('1900-01-01');
                const end = dateFilter.end ? new Date(dateFilter.end) : new Date('2100-01-01');
                end.setHours(23, 59, 59, 999);

                const checkDate = (dateStr) => {
                    if (!dateStr) return false;
                    const d = new Date(dateStr);
                    return d >= start && d <= end;
                };

                const datesToCheck = dateFilter.type === 'all'
                    ? dueWorkstreams.map(workstream => client[workstream.dueField])
                    : [client[getWorkstreamByKey(config, dateFilter.type)?.dueField]];
                matchesDate = datesToCheck.some(checkDate);
            }

            // Work Status Filter using config
            let matchesWorkStatus = true;
            statusWorkstreams.forEach(workstream => {
                if (workStatusFilter[workstream.key] !== 'all' && client[workstream.statusField] !== workStatusFilter[workstream.key]) {
                    matchesWorkStatus = false;
                }
            });

            // Work Plan Filter - separate for each work type
            let matchesWorkPlan = true;
            planWorkstreams.forEach(workstream => {
                if (planFilters[workstream.key] !== 'all') {
                    const plan = client[workstream.planField] || 'Unscheduled';
                    if (plan !== planFilters[workstream.key]) {
                        matchesWorkPlan = false;
                    }
                }
            });

            // Notes Filter
            let matchesNotes = true;
            if (filterNotes === 'has_notes') {
                if (!client.notes || !client.notes.trim()) matchesNotes = false;
            }

            // KPI due-month filter (workstream-scoped)
            let matchesKpiDueMonth = true;
            if (kpiDueMonthFilter !== 'all' && selectedKpiWorkstream?.dueField) {
                const clientMonth = getMonthKey(client[selectedKpiWorkstream.dueField]);
                matchesKpiDueMonth = clientMonth === kpiDueMonthFilter;
            }

            // KPI drilldown filter (workstream-scoped)
            let matchesKpiStage = true;
            if (kpiStageFilter !== 'all' && selectedKpiWorkstream?.statusField) {
                const rawStatus = String(client[selectedKpiWorkstream.statusField] || '').toLowerCase().trim();
                const isNotStarted = !rawStatus || rawStatus.includes('not started') || rawStatus.includes('not_started') || rawStatus === 'new';
                const isCompleted = rawStatus.includes('complet') || rawStatus.includes('filed') || rawStatus.includes('submit') || rawStatus.includes('approved') || rawStatus.includes('done');
                const isInProgress = !isNotStarted && !isCompleted;
                const selectedDueDays = selectedKpiWorkstream?.dueField
                    ? getDaysRemaining(client[selectedKpiWorkstream.dueField])
                    : null;

                if (kpiStageFilter === 'not_started') matchesKpiStage = isNotStarted;
                if (kpiStageFilter === 'in_progress') matchesKpiStage = isInProgress;
                if (kpiStageFilter === 'completed') matchesKpiStage = isCompleted;
                if (kpiStageFilter === 'overdue') matchesKpiStage = selectedDueDays !== null && selectedDueDays < 0;
            }

            // KPI staff drilldown filter (workstream-scoped)
            let matchesKpiStaff = true;
            if (kpiStaffFilter !== 'all' && selectedKpiWorkstream?.staffField) {
                const staffValue = client[selectedKpiWorkstream.staffField];
                matchesKpiStaff = String(staffValue ?? '') === String(kpiStaffFilter);
            }

            return matchesSearch && matchesStaff && matchesStatus && matchesDate && matchesWorkStatus && matchesWorkPlan && matchesNotes && matchesKpiDueMonth && matchesKpiStage && matchesKpiStaff;
        });
    }, [clients, config, dateFilter, dueWorkstreams, filterNotes, filterStaff, kpiDueMonthFilter, kpiStageFilter, kpiStaffFilter, planFilters, searchTerm, selectedKpiWorkstream, statusFilter, statusWorkstreams, workStatusFilter, planWorkstreams]);

    // Sorting & Pagination
    const sortedClients = useMemo(() => {
        const items = [...filteredClients];
        if (sortConfig.key) {
            items.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];
                if (typeof aValue === 'string') aValue = aValue.toLowerCase();
                if (typeof bValue === 'string') bValue = bValue.toLowerCase();
                if (!aValue) aValue = '';
                if (!bValue) bValue = '';
                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return items;
    }, [filteredClients, sortConfig]);

    const paginatedClients = useMemo(() => {
        if (itemsPerPage === 'All') return sortedClients;
        const startIndex = (currentPage - 1) * itemsPerPage;
        return sortedClients.slice(startIndex, startIndex + itemsPerPage);
    }, [sortedClients, currentPage, itemsPerPage]);

    const totalPages = itemsPerPage === 'All' ? 1 : Math.ceil(sortedClients.length / itemsPerPage);

    // Keyboard Shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Only handle shortcuts when not typing in inputs
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.contentEditable === 'true') {
                return;
            }

            if (e.ctrlKey || e.metaKey) {
                switch (e.key.toLowerCase()) {
                    case 'a':
                        e.preventDefault();
                        if (isBulkEditMode) {
                            setSelectedClients(new Set(paginatedClients.map(c => c.id)));
                        }
                        break;
                    case 'd':
                        e.preventDefault();
                        setSelectedClients(new Set());
                        break;
                    case 'e':
                        e.preventDefault();
                        setIsBulkEditMode(!isBulkEditMode);
                        if (isBulkEditMode) setSelectedClients(new Set());
                        break;
                    case 'f':
                        e.preventDefault();
                        // Focus search input
                        const searchInput = document.querySelector('input[placeholder*="search" i]');
                        if (searchInput) searchInput.focus();
                        break;
                    case 'n':
                        e.preventDefault();
                        if (config.actions?.canAdd) {
                            setShowAddClientModal(true);
                        }
                        break;
                }
            } else if (e.key === 'Escape') {
                // Close modals
                setShowContactsTable(false);
                setShowNotesTable(false);
                setShowDownloadModal(false);
                setShowPrintModal(false);
                setShowEmailModal(false);
                setShowMoveDropdown(false);
                setShowHmrcSyncHistory(false);
                setShowBulkAssignModal(false);
                setShowBulkStatusModal(false);
                setShowBulkServicesModal(false);
                setShowAddClientModal(false);
                setSelectedClientForModal(null);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isBulkEditMode, paginatedClients, config.actions?.canAdd]);

    // Actions
    const handleUpdate = async (id, field, value) => {
        // Skip fields that live in source tables (view-derived, not writable here)
        if (config.readonlyFields?.includes(field)) return;

        let targetIds = [id];
        if (isBulkEditMode && selectedClients.has(id)) {
            targetIds = Array.from(selectedClients);
        }

        const mainField = config.fieldMappings?.mainField;
        const numberField = config.fieldMappings?.numberField;
        setClients(prev => prev.map(c => {
            if (targetIds.includes(c.id)) {
                const updates = {
                    [field]: value,
                    ...(field === mainField ? { name: value } : {}),
                    ...(field === numberField ? { number: value } : {})
                };
                return { ...c, ...updates };
            }
            return c;
        }));
        
        await supabase.from(config.tableName).update({ [field]: value }).in('id', targetIds);
    };

    const handleSaveDetails = async (clientId, updatedData, applyToAll = false, changedFields = []) => {
        const payload = {};
        
        if (applyToAll && changedFields.length > 0) {
            changedFields.forEach(key => {
                if (updatedData.hasOwnProperty(key)) {
                    payload[key] = updatedData[key];
                }
            });
        } else {
            Object.assign(payload, updatedData);
        }

        // Strip fields that don't exist in the write table:
        //  - config.readonlyFields: view-derived identity/contact fields
        //  - virtual aliases added in fetchData: name, number, hmrc_sync_health
        const nonWritable = [
            'name', 'number', 'hmrc_sync_health',
            ...(config.readonlyFields || [])
        ];
        nonWritable.forEach(f => { delete payload[f]; });

        if (Object.keys(payload).length === 0) return;

        let targetIds = [clientId];
        if (applyToAll && selectedClients.size > 0) {
            const allIds = new Set(selectedClients);
            allIds.add(clientId);
            targetIds = Array.from(allIds);
        }

        setClients(prev => prev.map(c => targetIds.includes(c.id) ? { ...c, ...payload } : c));
        await supabase.from(config.tableName).update(payload).in('id', targetIds);
    };

    const handleBulkServiceUpdate = async (serviceUpdates) => {
        if (!serviceUpdates || Object.keys(serviceUpdates).length === 0) return;
        if (selectedClients.size === 0) return;

        const targetIds = Array.from(selectedClients);

        // Optimistic UI update
        setClients(prev => prev.map(c => targetIds.includes(c.id) ? { ...c, ...serviceUpdates } : c));

        if (selectedClientForModal && targetIds.includes(selectedClientForModal.id)) {
            setSelectedClientForModal(prev => ({ ...prev, ...serviceUpdates }));
        }

        const { error } = await supabase
            .from(config.tableName)
            .update(serviceUpdates)
            .in('id', targetIds);

        if (error) throw new Error(error.message);

        alert('Engaged services updated successfully.');
        await fetchData();
    };

    const handleSendEmail = async (emailData) => {
        const { to, cc, bcc, subject, html, attachments } = emailData;
        const { data, error } = await supabase.functions.invoke('send-email', {
            body: {
                to: to.map(r => r.email),
                cc: cc.map(r => r.email),
                bcc: bcc.map(r => r.email),
                subject: subject,
                html: html,
                attachments: attachments
            }
        });

        if (error) throw new Error(error.message);
        if (data?.error) throw new Error(data.error);
        
        alert('Email sent successfully!');
    };

    const handleOpenEmailModal = () => {
        const selectedData = clients.filter(c => selectedClients.has(c.id));
        const validRecipients = selectedData
            .filter(c => c.pc_email && c.pc_email.includes('@'))
            .map(c => ({ name: c.pc_fname || c.name, email: c.pc_email }));

        if (validRecipients.length === 0) {
            alert("No selected clients have valid email addresses.");
            return;
        }

        setEmailRecipients(validRecipients);
        setSelectedClientData(selectedData);
        setShowEmailModal(true);
    };

    const handleClearFilters = () => {
        setStatusFilter({ category: 'all', type: 'all' });
        setDateFilter({ type: 'all', start: '', end: '' });
        setWorkStatusFilter(buildWorkstreamState(config, 'all', workstream => Boolean(workstream.statusField)));
        setPlanFilters(buildWorkstreamState(config, 'all', workstream => Boolean(workstream.planField)));
        setFilterNotes('all');
        setFilterStaff('all');
        setSearchTerm('');
        setSelectedClients(new Set());
        setIsBulkEditMode(false);
    };

    const handleAddClient = async (clientData) => {
        const clientRows = Array.isArray(clientData) ? clientData : [clientData];
        const numberField = config.fieldMappings?.numberField || 'number';
        const mainField = config.fieldMappings?.mainField || 'name';
        const minLength = config.validation?.numberMinLength || 1;

        const invalidLengths = clientRows.filter(row => String(row[numberField] || '').trim().length < minLength);
        if (invalidLengths.length > 0) {
            const invalidNumbers = invalidLengths.map(row => `"${row[numberField]}" (${String(row[numberField]).length} chars)`).join(', ');
            throw new Error(`${entity.numberLabel}(s) must be at least ${minLength} characters long: ${invalidNumbers}`);
        }
        
        const clientNumbers = clientRows.map(row => row[numberField]);
        const { data: existingClients, error: checkError } = await supabase
            .from(config.tableName)
            .select(`${numberField}, ${mainField}`)
            .in(numberField, clientNumbers);

        if (checkError) {
            console.error('Error checking existing records:', checkError);
            throw new Error(checkError.message);
        }

        if (existingClients && existingClients.length > 0) {
            const duplicateNumbers = existingClients.map(row => `${row[numberField]} (${row[mainField]})`).join(', ');
            throw new Error(`The following ${entity.numberLabel.toLowerCase()}(s) already exist in the database: ${duplicateNumbers}`);
        }

        const defaults = config.newRecordDefaults || {};
        const newClients = clientRows.map(row => {
            const record = Object.fromEntries(
                Object.entries(row).filter(([, value]) => String(value ?? '').trim() !== '')
            );
            Object.entries(defaults).forEach(([key, value]) => {
                if (!record[key]) {
                    record[key] = typeof value === 'function' ? value() : value;
                }
            });
            return record;
        });

        const { data, error } = await supabase
            .from(config.tableName)
            .insert(newClients)
            .select();

        if (error) {
            console.error('Error adding records:', error);
            throw new Error(error.message);
        }

        if (data && data.length > 0) {
            setClients(prev => [...prev, ...data.map(record => ({
                ...record,
                name: record[mainField] || '',
                number: record[numberField] || ''
            }))]);
            const message = data.length === 1 
                ? `${entity.singular} "${data[0][mainField]}" added successfully!`
                : `${data.length} ${entity.plural.toLowerCase()} added successfully!`;
            alert(message);
            await fetchData();
        }
    };

    // Relational service enrolment — toggles the source-table service flag ON.
    // DB triggers then auto-create the service workflow row via ON CONFLICT
    // DO NOTHING, so the module's read view picks up the client immediately.
    const handleEnrolClients = async (clientsToEnrol) => {
        if (!clientsToEnrol || clientsToEnrol.length === 0) return;

        const enrollServiceField = config?.enrollServiceField || 'svc_bookkeeping';

        // Group source IDs by source type
        const groups = { company: [], charity: [], individual: [] };
        clientsToEnrol.forEach(c => {
            if (groups[c.source_type] !== undefined) {
                groups[c.source_type].push(c.source_id);
            }
        });

        const TABLE_MAP = {
            company:    'clients_coy',
            charity:    'clients_charity',
            individual: 'clients_individual',
            unregistered: 'clients_unregistered',
            partnership:  'clients_partnership',
        };

        const updates = Object.entries(groups)
            .filter(([, ids]) => ids.length > 0)
            .map(([type, ids]) =>
                supabase
                    .from(TABLE_MAP[type])
                    .update({ [enrollServiceField]: true })
                    .in('id', ids)
            );

        const results = await Promise.all(updates);
        const firstError = results.find(r => r.error)?.error;
        if (firstError) throw new Error(firstError.message);

        const count = clientsToEnrol.length;
        const msg = count === 1
            ? `${clientsToEnrol[0].name} enrolled in ${config?.label || entity.singular}.`
            : `${count} clients enrolled in ${config?.label || entity.plural}.`;
        alert(msg);
        await fetchData();
    };

    const handleSyncExternalData = async () => {
        if (isSyncingExternal || !config.syncConfig?.functionName) return;
        
        setIsSyncingExternal(true);
        
        try {
            const invokeSync = async () => supabase.functions.invoke(config.syncConfig.functionName);

            let { error } = await invokeSync();

            if (error) {
                const errorText = String(error?.message || '').toLowerCase();
                const statusCode = Number(error?.context?.status || 0);
                const looksLikeInvalidJwt = errorText.includes('invalid jwt') || errorText.includes('401') || statusCode === 401;

                if (looksLikeInvalidJwt) {
                    const { data: sessionData } = await supabase.auth.getSession();
                    if (!sessionData?.session) {
                        throw new Error('Your session has expired. Please sign in again and retry sync.');
                    }

                    const { error: refreshError } = await supabase.auth.refreshSession();
                    if (refreshError) {
                        throw new Error('Your session expired and could not be refreshed. Please sign in again.');
                    }

                    const retry = await invokeSync();
                    error = retry.error;
                }
            }

            if (error) throw error;
            
            alert(config.syncConfig.successMessage || `${entity.syncLabel} triggered successfully.`);
            
            setTimeout(async () => {
                await fetchData();
                setIsSyncingExternal(false);
            }, 3000);
        } catch (err) {
            let errorDetail = err?.message || 'Unknown error';

            if (err?.context && typeof err.context.json === 'function') {
                try {
                    const payload = await err.context.json();
                    if (payload?.error) {
                        errorDetail = payload.error;
                    } else {
                        errorDetail = JSON.stringify(payload);
                    }
                } catch {
                    // Keep fallback message if response body is not JSON
                }
            }

            console.error(`Error running ${config.syncConfig?.functionName}:`, err);
            alert(`Failed to trigger ${config.syncConfig?.errorLabel || entity.syncLabel}: ${errorDetail}`);
            setIsSyncingExternal(false);
        }
    };

    // ── Refresh the currently-open modal with the latest row from the DB ──────
    const refreshModalClient = async (clientId) => {
        try {
            const { data } = await supabase
                .from(config.tableName)
                .select('*')
                .eq('id', clientId)
                .single();
            if (data) setSelectedClientForModal(data);
            await fetchData();
        } catch (_) { /* non-fatal */ }
    };

    const getFunctionErrorDetail = async (err, fallback = 'Unknown error') => {
        let detail = err?.message || fallback;
        if (err?.context && typeof err.context.json === 'function') {
            try {
                const payload = await err.context.json();
                if (payload?.error) {
                    detail = payload.error;
                    if (payload?.clientActionUrl) {
                        detail += `\n\nClient consent link: ${payload.clientActionUrl}`;
                    }
                } else if (payload && typeof payload === 'object') {
                    detail = JSON.stringify(payload);
                }
            } catch {
                // keep fallback detail
            }
        }
        return detail;
    };

    // ── Phase 2: HMRC Agent Authorisation ────────────────────────────────────
    const handleRequestAuthorisation = async (clientId, postcode) => {
        if (isRequestingAuth) return;
        if (!postcode?.trim()) {
            alert("Please enter the client's postcode before requesting authorisation.");
            return;
        }
        setIsRequestingAuth(true);
        try {
            const { data, error } = await supabase.functions.invoke('hmrc-agent-authorisation', {
                body: { mode: 'create_invitation', clientId, postcode: postcode.trim().toUpperCase() },
            });
            if (error) throw error;
            if (!data?.success) throw new Error(data?.error || 'Invitation request failed.');
            alert(`Authorisation request sent to HMRC (ID: ${data.invitationId}).\n\nThe client must accept it by logging in to their Government Gateway account.\n\nUse the "Open Client Link" button in the modal to launch the HMRC consent page.`);
            await refreshModalClient(clientId);
        } catch (err) {
            const detail = await getFunctionErrorDetail(err, 'Unknown error');
            console.error('Request authorisation failed:', err);
            alert(`Failed to request authorisation: ${detail}`);
        } finally {
            setIsRequestingAuth(false);
        }
    };

    const handleCheckInvitationStatus = async (clientId) => {
        if (isCheckingInvite) return;
        setIsCheckingInvite(true);
        try {
            const { data, error } = await supabase.functions.invoke('hmrc-agent-authorisation', {
                body: { mode: 'check_status', clientId },
            });
            if (error) throw error;
            if (!data?.success) throw new Error(data?.error || 'Status check failed.');
            const linkSuffix = data?.clientActionUrl ? `\n\nClient consent link available.` : '';
            alert(`HMRC invitation status: ${data.status.toUpperCase()}${linkSuffix}`);
            await refreshModalClient(clientId);
        } catch (err) {
            const detail = await getFunctionErrorDetail(err, 'Unknown error');
            console.error('Check invitation status failed:', err);
            alert(`Failed to check invitation status: ${detail}`);
        } finally {
            setIsCheckingInvite(false);
        }
    };

    const handleSandboxAccept = async (clientId) => {
        if (isCheckingInvite || isRequestingAuth) return;
        setIsCheckingInvite(true);
        try {
            const { data, error } = await supabase.functions.invoke('hmrc-agent-authorisation', {
                body: { mode: 'sandbox_accept', clientId },
            });
            if (error) throw error;
            if (!data?.success) throw new Error(data?.error || 'Sandbox accept failed.');
            const msg = data?.simulated
                ? 'Sandbox acceptance was simulated (HMRC test-support endpoint unavailable).\n\nUse Check Status to verify real HMRC acceptance, or connect this client via Client for direct access.'
                : 'Invitation accepted in sandbox! The agent is now authorised for this client.\n\nRun Sync HMRC Data to confirm.';
            alert(msg);
            await refreshModalClient(clientId);
        } catch (err) {
            const detail = await getFunctionErrorDetail(err, 'Unknown error');
            console.error('Sandbox accept failed:', err);
            if (detail?.toLowerCase().includes('client consent link')) {
                alert(`Sandbox accept failed. Accept the invitation manually using the client consent link below:\n\n${detail}`);
            } else {
                alert(`Sandbox accept failed: ${detail}`);
            }
        } finally {
            setIsCheckingInvite(false);
        }
    };

    const handleOpenInviteLink = async (clientId) => {
        if (isCheckingInvite || isRequestingAuth) return;
        setIsCheckingInvite(true);
        try {
            const { data, error } = await supabase.functions.invoke('hmrc-agent-authorisation', {
                body: { mode: 'check_status', clientId },
            });
            if (error) throw error;
            if (!data?.success) throw new Error(data?.error || 'Failed to retrieve invitation link.');
            if (!data?.clientActionUrl) {
                throw new Error('HMRC did not return a client consent link for this invitation.');
            }
            window.open(data.clientActionUrl, '_blank', 'noopener,noreferrer');
            await refreshModalClient(clientId);
        } catch (err) {
            const detail = await getFunctionErrorDetail(err, 'Unknown error');
            console.error('Open invitation link failed:', err);
            alert(`Failed to open client consent link: ${detail}`);
        } finally {
            setIsCheckingInvite(false);
        }
    };

    const handleConnectHmrc = async (clientId, connectMode = 'agent') => {
        if (isConnectingHmrc || config?.id !== 'individual') return;

        if (hmrcEnvStatus?.isMixedEnvironment) {
            alert('HMRC configuration is blocked: mixed sandbox/production URLs detected. Fix HMRC_BASE_URL, HMRC_AUTH_URL and HMRC_TOKEN_URL to the same environment before connecting.');
            return;
        }

        setIsConnectingHmrc(true);
        try {
            const mode = connectMode === 'client' ? 'start' : 'start_agent';
            const { data, error } = await supabase.functions.invoke('hmrc-auth-connect', {
                body: {
                    mode,
                    clientId,
                },
            });

            if (error) throw error;

            const authorizationUrl = data?.authorizationUrl;
            if (!authorizationUrl) {
                throw new Error('HMRC authorization URL was not returned.');
            }

            window.location.assign(authorizationUrl);
        } catch (err) {
            const detail = err?.message || 'Unknown HMRC connect error';
            console.error('HMRC connect init error:', err);
            alert(`Failed to start HMRC connection: ${detail}`);
            setIsConnectingHmrc(false);
        }
    };

    const handleMoveMonth = async (clientId, newMonth) => {
        const columnToUpdate = activePlannerWorkstream?.planField;
        if (!columnToUpdate) return;
        
        const { error } = await supabase
            .from(config.tableName)
            .update({ [columnToUpdate]: newMonth })
            .eq('id', clientId);
        
        if (!error) {
            await fetchData();
        } else {
            console.error('Error moving client:', error);
        }
    };

    const handleBulkMove = async (targetMonth) => {
        const ids = Array.from(selectedClients);
        await Promise.all(ids.map(id => handleMoveMonth(id, targetMonth)));
        setSelectedClients(new Set());
    };

    const handleBulkAssign = async (workstreamKey, staffId) => {
        const workstream = config.workstreams.find(ws => ws.key === workstreamKey);
        if (!workstream) return;

        const ids = Array.from(selectedClients);
        const updates = { [workstream.staffField]: staffId };

        await Promise.all(ids.map(id =>
            supabase.from(config.tableName).update(updates).eq('id', id)
        ));

        await fetchData();
        setSelectedClients(new Set());
    };

    const handleBulkStatusUpdate = async (workstreamKey, status) => {
        const workstream = config.workstreams.find(ws => ws.key === workstreamKey);
        if (!workstream) return;

        const ids = Array.from(selectedClients);
        const updates = { [workstream.statusField]: status };

        await Promise.all(ids.map(id =>
            supabase.from(config.tableName).update(updates).eq('id', id)
        ));

        await fetchData();
        setSelectedClients(new Set());
    };

    const handleSaveNotes = async (clientId, newNotes) => {
        const { error } = await supabase
            .from(config.tableName)
            .update({ notes: newNotes })
            .eq('id', clientId);
        
        if (!error) {
            setClients(prev => prev.map(c => c.id === clientId ? { ...c, notes: newNotes } : c));
        }
    };

    const handleSort = (columnKey) => {
        if (sortColumn === columnKey) {
            // Toggle direction if same column
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // New column, default to ascending
            setSortColumn(columnKey);
            setSortDirection('asc');
        }
    };

    const handleApplyPreset = (presetId) => {
        if (!presetId) {
            // Clear all filters
            setActivePreset(null);
            setStatusFilter({ category: 'all', type: 'all' });
            setDateFilter({ type: 'all', start: '', end: '' });
            setWorkStatusFilter(buildWorkstreamState(config, 'all', workstream => Boolean(workstream.statusField)));
            setPlanFilters(buildWorkstreamState(config, 'all', workstream => Boolean(workstream.planField)));
            setFilterNotes('all');
            setFilterStaff('all');
            setSearchTerm('');
            setKpiStageFilter('all');
            setKpiDueMonthFilter('all');
            setKpiStaffFilter('all');
            return;
        }

        setStatusFilter({ category: 'all', type: 'all' });
        setDateFilter({ type: 'all', start: '', end: '' });
        setWorkStatusFilter(buildWorkstreamState(config, 'all', workstream => Boolean(workstream.statusField)));
        setPlanFilters(buildWorkstreamState(config, 'all', workstream => Boolean(workstream.planField)));
        setFilterNotes('all');
        setFilterStaff('all');
        setSearchTerm('');
        setActivePreset(presetId);
        setKpiStageFilter('all');
        setKpiDueMonthFilter('all');
        setKpiStaffFilter('all');
        const today = new Date();
        const toYmd = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        const getFriday = (date) => {
            const base = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            const day = base.getDay(); // 0=Sun ... 5=Fri
            const delta = (5 - day + 7) % 7;
            const friday = new Date(base);
            friday.setDate(base.getDate() + delta);
            return friday;
        };

        const weekEndFriday = getFriday(today);
        const nextWeekEndFriday = new Date(weekEndFriday);
        nextWeekEndFriday.setDate(weekEndFriday.getDate() + 7);

        const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        const nextMonthStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        const nextMonthEnd = new Date(today.getFullYear(), today.getMonth() + 2, 0);
        const monthAfterNextStart = new Date(today.getFullYear(), today.getMonth() + 2, 1);
        const monthAfterNextEnd = new Date(today.getFullYear(), today.getMonth() + 3, 0);

        switch (presetId) {
            case 'overdue':
                setStatusFilter({ category: 'overdue', type: 'all' });
                break;
            case 'on-track':
                setStatusFilter({ category: 'ontrack', type: 'all' });
                break;
            case 'due-this-week':
                setDateFilter({
                    type: 'all',
                    start: toYmd(today),
                    end: toYmd(weekEndFriday)
                });
                setStatusFilter({ category: 'all', type: 'all' });
                break;
            case 'due-in-2-weeks': {
                const start = new Date(weekEndFriday);
                start.setDate(start.getDate() + 1);
                setDateFilter({
                    type: 'all',
                    start: toYmd(start),
                    end: toYmd(nextWeekEndFriday)
                });
                setStatusFilter({ category: 'all', type: 'all' });
                break;
            }
            case 'due-this-month':
                setDateFilter({
                    type: 'all',
                    start: toYmd(today),
                    end: toYmd(thisMonthEnd)
                });
                setStatusFilter({ category: 'all', type: 'all' });
                break;
            case 'due-next-month':
                setDateFilter({
                    type: 'all',
                    start: toYmd(nextMonthStart),
                    end: toYmd(nextMonthEnd)
                });
                setStatusFilter({ category: 'all', type: 'all' });
                break;
            case 'due-next-2-months':
                setDateFilter({
                    type: 'all',
                    start: toYmd(monthAfterNextStart),
                    end: toYmd(monthAfterNextEnd)
                });
                setStatusFilter({ category: 'all', type: 'all' });
                break;
            case 'unassigned':
                setFilterStaff('unassigned');
                break;
            case 'with-notes':
                setFilterNotes('has_notes');
                break;
            case 'mtd-due-30-days': {
                const next30 = new Date(today);
                next30.setDate(today.getDate() + 30);
                setDateFilter({
                    type: 'all',
                    start: today.toISOString().split('T')[0],
                    end: next30.toISOString().split('T')[0]
                });
                setStatusFilter({ category: 'all', type: 'all' });
                break;
            }
            case 'mtd-not-started': {
                const notStartedState = buildWorkstreamState(config, 'all', ws => Boolean(ws.statusField));
                statusWorkstreams.forEach(ws => { notStartedState[ws.key] = 'Not Started'; });
                setWorkStatusFilter(notStartedState);
                setStatusFilter({ category: 'all', type: 'all' });
                break;
            }
            case 'mtd-overdue-not-started': {
                const notStartedState = buildWorkstreamState(config, 'all', ws => Boolean(ws.statusField));
                statusWorkstreams.forEach(ws => { notStartedState[ws.key] = 'Not Started'; });
                setWorkStatusFilter(notStartedState);
                setStatusFilter({ category: 'overdue', type: 'all' });
                break;
            }
            case 'mtd-mandated': {
                setSearchTerm('MTD Mandated');
                break;
            }
        }
    };

    useEffect(() => {
        if (!dashboardDrilldown?.moduleId || dashboardDrilldown.moduleId !== config?.id) return;

        const nonce = dashboardDrilldown?.nonce;
        if (!nonce || lastDashboardDrilldownNonceRef.current === nonce) return;
        lastDashboardDrilldownNonceRef.current = nonce;

        const filterId = String(dashboardDrilldown?.filter || 'all').toLowerCase();
        const drilldownStaffId = dashboardDrilldown?.staffId !== undefined && dashboardDrilldown?.staffId !== null
            ? String(dashboardDrilldown.staffId)
            : null;
        const drilldownStaffName = dashboardDrilldown?.staffName || null;
        const sourceLabel = dashboardDrilldown?.source === 'staff-workload' ? 'Staff Workload' : 'Dashboard';
        const labelMap = {
            all: 'All records',
            overdue: 'Overdue',
            'due-this-week': 'Due this week',
            'due-in-2-weeks': 'Due in 2 weeks',
            'due-this-month': 'Due this month',
            'due-next-month': 'Due next month',
            'due-next-2-months': 'Due in 2 months',
            'on-track': 'On track',
        };

        setDashboardContextChip({
            source: sourceLabel,
            filter: filterId,
            label: `${labelMap[filterId] || 'Filtered view'}${drilldownStaffName ? ` • ${drilldownStaffName}` : ''}`,
        });

        if (filterId === 'overdue') {
            handleApplyPreset('overdue');
            if (drilldownStaffId) setFilterStaff(drilldownStaffId);
            return;
        }

        if (filterId === 'due-this-week') {
            handleApplyPreset('due-this-week');
            if (drilldownStaffId) setFilterStaff(drilldownStaffId);
            return;
        }

        if (filterId === 'due-in-2-weeks') {
            handleApplyPreset('due-in-2-weeks');
            if (drilldownStaffId) setFilterStaff(drilldownStaffId);
            return;
        }

        if (filterId === 'due-this-month') {
            handleApplyPreset('due-this-month');
            if (drilldownStaffId) setFilterStaff(drilldownStaffId);
            return;
        }

        if (filterId === 'due-next-month') {
            handleApplyPreset('due-next-month');
            if (drilldownStaffId) setFilterStaff(drilldownStaffId);
            return;
        }

        if (filterId === 'due-next-2-months') {
            handleApplyPreset('due-next-2-months');
            if (drilldownStaffId) setFilterStaff(drilldownStaffId);
            return;
        }

        if (filterId === 'on-track') {
            handleApplyPreset('on-track');
            if (drilldownStaffId) setFilterStaff(drilldownStaffId);
            return;
        }

        handleApplyPreset(null);
        if (drilldownStaffId) setFilterStaff(drilldownStaffId);
    }, [config?.id, dashboardDrilldown]);

    const clearDashboardContextChip = () => {
        setDashboardContextChip(null);
        handleApplyPreset(null);
    };

    const hasActiveWorkStatusFilters = Object.values(workStatusFilter).some(value => value !== 'all');

    // UI Components
    const StatBox = ({ label, value, isActive, onClick, colorClass }) => (
        <div onClick={(e) => { e.stopPropagation(); onClick(); }} className={`flex flex-col items-center p-0.5 rounded cursor-pointer transition-colors ${isActive ? 'bg-slate-100 ring-1 ring-slate-200' : 'hover:bg-slate-50'}`}>
            <div className={`text-base font-bold leading-none mb-0.5 ${colorClass}`}>{value}</div>
            <div className="text-[9px] text-slate-400 font-medium uppercase leading-none">{label}</div>
        </div>
    );

    const renderStatusCard = ({ category, title, icon: Icon, borderClass, totalColorClass }) => {
        const cardStats = stats[category];

        return (
            <div
                onClick={() => setStatusFilter({ category, type: 'all' })}
                className={`bg-white p-2 rounded-lg border shadow-sm cursor-pointer transition-all ${statusFilter.category === category ? `${borderClass} ring-1` : 'border-slate-200'}`}
            >
                <div className="flex justify-between items-center mb-1.5">
                    <span className="text-[10px] font-semibold text-slate-500 uppercase">{title}</span>
                    <Icon className={`w-3.5 h-3.5 ${totalColorClass}`} />
                </div>
                <div className="grid gap-0.5" style={{ gridTemplateColumns: `repeat(${dueWorkstreams.length + 1}, minmax(0, 1fr))` }}>
                    <StatBox
                        label="Total"
                        value={cardStats.total}
                        colorClass={totalColorClass}
                        isActive={statusFilter.category === category && statusFilter.type === 'all'}
                        onClick={() => setStatusFilter({ category, type: 'all' })}
                    />
                    {dueWorkstreams.map(workstream => (
                        <StatBox
                            key={`${category}-${workstream.key}`}
                            label={workstream.shortLabel || workstream.label}
                            value={cardStats.byWorkstream[workstream.key] || 0}
                            colorClass="text-slate-700"
                            isActive={statusFilter.category === category && statusFilter.type === workstream.key}
                            onClick={() => setStatusFilter({ category, type: workstream.key })}
                        />
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="flex-1 min-h-0 flex flex-col bg-white rounded-lg shadow-sm border border-slate-200 relative min-h-[400px]">
            {config?.id === 'individual' && hmrcEnvStatus && (
                <div className={`px-3 py-1.5 border-b text-xs font-medium flex items-center gap-2 ${hmrcEnvStatus.isMixedEnvironment ? 'bg-red-50 border-red-200 text-red-700' : hmrcEnvStatus.hmrcEnvironment === 'sandbox' ? 'bg-amber-50 border-amber-200 text-amber-700' : hmrcEnvStatus.hmrcEnvironment === 'production' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                    <AlertCircle className="w-3.5 h-3.5" />
                    {hmrcEnvStatus.isMixedEnvironment
                        ? 'HMRC Guard: Mixed sandbox/production URLs detected. OAuth is blocked until configuration is fixed.'
                        : hmrcEnvStatus.hmrcEnvironment === 'sandbox'
                            ? 'HMRC Environment: Sandbox (use HMRC sandbox agent/test credentials only).'
                            : hmrcEnvStatus.hmrcEnvironment === 'production'
                                ? 'HMRC Environment: Production (use real agent credentials only).'
                                : 'HMRC Environment: Unknown.'}
                </div>
            )}
            {/* HMRC Connection Health Bar (Individual module only) */}
            {config?.id === 'individual' && (
                <>
                    <TokenExpiryWarning config={config} />
                    <div className="px-3 py-2 border-b border-slate-200 bg-blue-50/50 flex items-center gap-4 flex-none">
                        <div className="flex items-center gap-1">
                            <BarChart3 className="w-4 h-4 text-blue-600" />
                            <span className="text-xs font-semibold text-slate-600">HMRC Connection Health:</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                                <span className="text-xs font-semibold text-emerald-700">{hmrcHealthStats.connected} Connected</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-amber-600"></span>
                                <span className="text-xs font-semibold text-amber-700">{hmrcHealthStats.notAuthorised} Not Authorised</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                                <span className="text-xs font-semibold text-slate-600">{hmrcHealthStats.needsReconnecting} Needs Setup</span>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowHmrcSyncHistory(true)}
                            className="ml-auto px-2 py-1 text-xs font-bold text-blue-700 bg-blue-100 hover:bg-blue-200 rounded transition-colors"
                        >
                            View Sync History
                        </button>
                    </div>
                </>
            )}
            {/* Stats Bar */}
            <div className="bg-slate-50 border-b border-slate-200 p-2 grid grid-cols-4 gap-2 flex-none">
                <div className={`bg-white p-2 rounded-lg border shadow-sm transition-all ${statusFilter.category === 'all' ? 'border-blue-500 ring-1 ring-blue-500 bg-blue-50/10' : 'border-slate-200'}`}>
                    <div className="flex justify-between items-center mb-1">
                        <span className="text-[10px] font-semibold text-slate-500 uppercase">{entity.totalLabel}</span>
                        <Users className="w-3.5 h-3.5 text-blue-600"/>
                    </div>
                    <div className="flex items-center justify-center gap-4">
                        <div onClick={() => setStatusFilter({ category: 'all', type: 'all' })} className="text-lg font-bold text-slate-800 cursor-pointer">{stats.total}</div>
                        <div className="flex items-center gap-1">
                            {config.actions?.canAdd && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowAddClientModal(true); }}
                                    className="flex items-center gap-0.5 px-2 py-1 text-[9px] font-bold text-white bg-blue-600 hover:bg-blue-700 rounded transition-all shadow-sm whitespace-nowrap"
                                    title={entity.addLabel}
                                >
                                    <span>+ {entity.addLabel}</span>
                                </button>
                            )}
                            {config.actions?.canSync && config.syncConfig?.functionName && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleSyncExternalData(); }}
                                    disabled={isSyncingExternal}
                                    className="flex items-center gap-0.5 px-2 py-1 text-[9px] font-bold text-white bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed rounded transition-all shadow-sm whitespace-nowrap"
                                    title={entity.syncLabel}
                                >
                                    {isSyncingExternal ? (
                                        <RefreshCw className="w-2.5 h-2.5 animate-spin" />
                                    ) : (
                                        <span>{entity.syncLabel}</span>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {renderStatusCard({ category: 'due14', title: 'Due (14 Days)', icon: Clock, borderClass: 'border-orange-500 ring-orange-500', totalColorClass: 'text-orange-600' })}
                {renderStatusCard({ category: 'dueSoon', title: 'Due (30 Days)', icon: Clock, borderClass: 'border-amber-500 ring-amber-500', totalColorClass: 'text-amber-600' })}
                {renderStatusCard({ category: 'overdue', title: 'Overdue', icon: AlertCircle, borderClass: 'border-red-500 ring-red-500', totalColorClass: 'text-red-600' })}
            </div>

            {dueWorkstreams.length > 0 && workstreamKpiStats && (
                <WorkstreamKpiStrip
                    selectorLabel={config?.kpiConfig?.selectorLabel || (config?.id === 'mtd_itsa' ? 'Period' : 'Workstream')}
                    workstreams={dueWorkstreams}
                    selectedWorkstreamKey={selectedKpiWorkstreamKey}
                    onSelectWorkstream={(key) => {
                        setSelectedKpiWorkstreamKey(key);
                        setCurrentPage(1);
                        setKpiStageFilter('all');
                        setKpiDueMonthFilter('all');
                        setKpiStaffFilter('all');
                    }}
                    showDueMonthFilter={true}
                    dueMonthLabel={config?.kpiConfig?.dueMonthLabel || 'Due Month'}
                    stageLabels={config?.kpiConfig?.stageLabels || {}}
                    progressTitle={config?.kpiConfig?.progressTitle || 'Progress'}
                    staffLoadTitle={config?.kpiConfig?.staffLoadTitle || 'Staff Load'}
                    dueMonthOptions={kpiDueMonthOptions}
                    selectedDueMonth={kpiDueMonthFilter}
                    onSelectDueMonth={(month) => {
                        setCurrentPage(1);
                        setKpiDueMonthFilter(month);
                    }}
                    stats={workstreamKpiStats}
                    activeFilter={kpiStageFilter}
                    onToggleFilter={(id) => {
                        setCurrentPage(1);
                        setKpiStageFilter(prev => (prev === id ? 'all' : id));
                    }}
                    activeStaffFilter={kpiStaffFilter}
                    onToggleStaffFilter={(staffId) => {
                        setCurrentPage(1);
                        setKpiStaffFilter(prev => (String(prev) === String(staffId) ? 'all' : String(staffId)));
                    }}
                    onClearFilter={() => {
                        setCurrentPage(1);
                        setKpiStageFilter('all');
                        setKpiDueMonthFilter('all');
                        setKpiStaffFilter('all');
                    }}
                />
            )}

            {dashboardContextChip && (
                <div className="px-3 py-2 border-b border-slate-200 bg-blue-50/60 flex items-center justify-between gap-3 flex-none">
                    <div className="flex items-center gap-2 min-w-0">
                        <div className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-blue-700">
                            <BarChart3 className="w-3.5 h-3.5" />
                            <span>{dashboardContextChip.source}</span>
                        </div>
                        <div className="text-xs text-slate-600 truncate">
                            Opened from {dashboardContextChip.source} with filter: <span className="font-semibold text-slate-800">{dashboardContextChip.label}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        {onReturnToDrilldownSource && (
                            <button
                                type="button"
                                onClick={onReturnToDrilldownSource}
                                className="inline-flex items-center gap-1 rounded-md border border-blue-300 bg-blue-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-blue-700 transition-colors"
                            >
                                <ArrowLeft className="w-3.5 h-3.5" />
                                Return
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={clearDashboardContextChip}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                            <XCircle className="w-3.5 h-3.5" />
                            Clear
                        </button>
                    </div>
                </div>
            )}

            {/* Toolbar */}
            <div className="bg-white p-2 border-b border-slate-200 flex gap-2 items-center flex-none overflow-x-auto no-scrollbar">
                <FilterPresets
                    onApplyPreset={handleApplyPreset}
                    activePreset={activePreset}
                    extraPresets={config?.id === 'mtd_itsa' ? [
                        { id: 'mtd-mandated',   label: 'Mandated Only',        description: 'Show MTD Mandated clients only',             color: 'bg-orange-100 text-orange-700 border-orange-200' },
                        { id: 'mtd-due-30-days',label: 'Due 30 Days',          description: 'Clients with obligations due within 30 days', color: 'bg-amber-100 text-amber-600 border-amber-200' },
                        { id: 'mtd-not-started',label: 'Not Started',          description: 'Clients with no quarter work started',        color: 'bg-slate-100 text-slate-600 border-slate-300' },
                        { id: 'mtd-overdue-not-started',label: 'Overdue + Not Started', description: 'Overdue obligations where work has not started', color: 'bg-rose-100 text-rose-700 border-rose-200' },
                    ] : []}
                    className="flex-shrink-0"
                />
                {/* Pagination Controls */}
                {viewMode === 'list' && (
                    <div className="flex items-center gap-1 bg-slate-50 p-1 rounded-lg border border-slate-200 h-9 flex-shrink-0">
                        <select 
                            value={itemsPerPage} 
                            onChange={(e) => { setItemsPerPage(e.target.value === 'All' ? 'All' : parseInt(e.target.value)); setCurrentPage(1); }}
                            className="text-xs border-none bg-transparent focus:ring-0 text-slate-600 cursor-pointer font-medium"
                        >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="All">All</option>
                        </select>
                        <button 
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="p-0.5 hover:bg-slate-200 disabled:text-slate-300 disabled:hover:bg-transparent rounded transition-colors"
                            title="Previous page"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="text-xs font-medium text-slate-600 px-1 whitespace-nowrap">{currentPage}</span>
                        <button 
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className="p-0.5 hover:bg-slate-200 disabled:text-slate-300 disabled:hover:bg-transparent rounded transition-colors"
                            title="Next page"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                )}

                <div className="flex bg-slate-100 p-1 rounded-md h-9">
                    <button 
                        onClick={() => { setViewMode('list'); setShowPlannerDropdown(false); }} 
                        className={`flex items-center gap-2 px-4 py-1.5 rounded text-xs font-bold ${viewMode === 'list' ? 'bg-white shadow-sm' : 'text-slate-500'}`}
                    >
                        <List className="w-3.5 h-3.5" /> List
                    </button>
                    {planWorkstreams.length > 0 && (
                        <div className="relative">
                            <button 
                                ref={(el) => {
                                    if (el && !plannerButtonRef.current) {
                                        plannerButtonRef.current = el;
                                    }
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (planWorkstreams.length === 1) {
                                        setPlannerType(planWorkstreams[0].key);
                                        setViewMode('planning');
                                        return;
                                    }
                                    setShowPlannerDropdown(prev => !prev);
                                }} 
                                className={`flex items-center gap-2 px-4 py-1.5 rounded text-xs font-bold ${viewMode === 'planning' ? 'bg-white shadow-sm' : 'text-slate-500'}`}
                            >
                                <LayoutGrid className="w-3.5 h-3.5" /> Planning
                                {viewMode === 'planning' && activePlannerWorkstream && (
                                    <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded uppercase font-bold ml-1">
                                        {activePlannerWorkstream.shortLabel || activePlannerWorkstream.label}
                                    </span>
                                )}
                            </button>
                            
                            {showPlannerDropdown && plannerButtonRef.current && planWorkstreams.length > 1 && (() => {
                                const rect = plannerButtonRef.current.getBoundingClientRect();
                                return createPortal(
                                <div 
                                    style={{ 
                                        position: 'fixed',
                                        top: `${rect.bottom + 4}px`,
                                        left: `${rect.left}px`
                                    }}
                                    className="bg-white rounded-lg shadow-2xl border-2 border-slate-300 py-1 z-[9999] min-w-[220px]">
                                {planWorkstreams.map(workstream => {
                                    const colors = getWorkstreamColorClasses(workstream);

                                    return (
                                        <button
                                            key={workstream.key}
                                            onClick={() => {
                                                setPlannerType(workstream.key);
                                                setViewMode('planning');
                                                setShowPlannerDropdown(false);
                                            }}
                                            className={`w-full text-left px-4 py-2 text-xs font-semibold transition-colors ${
                                                plannerType === workstream.key && viewMode === 'planning'
                                                    ? 'bg-slate-100 text-slate-900'
                                                    : 'text-slate-700 hover:bg-slate-50'
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
                                                {workstream.label} ({workstream.shortLabel || workstream.key.toUpperCase()}) Planner
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>,
                            document.body
                            );
                        })()}
                        </div>
                    )}
                    
                    {viewMode === 'list' && (
                        <button onClick={() => { setIsBulkEditMode(!isBulkEditMode); if(isBulkEditMode) setSelectedClients(new Set()); }} className={`flex items-center gap-1.5 px-3 py-1.5 ml-1 rounded text-xs font-bold border ${isBulkEditMode ? 'bg-purple-100 text-purple-700 border-purple-300' : 'bg-white text-slate-500'}`}><CheckSquare className="w-3.5 h-3.5"/> Bulk Edit</button>
                    )}
                </div>

                {/* Date Range Filter */}
                <div className="flex items-center gap-2 bg-slate-50 p-1.5 rounded-lg border border-slate-200 h-9 flex-shrink-0">
                    <CalendarDays className="w-4 h-4 text-slate-400" />
                    <select className="text-xs bg-transparent border-none focus:ring-0 text-slate-600 cursor-pointer font-medium" value={dateFilter.type} onChange={(e) => setDateFilter({...dateFilter, type: e.target.value})}>
                        <option value="all">All Dates</option>
                        {dueWorkstreams.map(workstream => <option key={workstream.key} value={workstream.key}>{workstream.label}</option>)}
                    </select>
                    <div className="h-4 w-px bg-slate-300 mx-1"></div>
                    <input type="date" className="text-xs border-none bg-transparent focus:ring-0 text-slate-600 w-[85px] font-medium cursor-pointer" value={dateFilter.start} onChange={(e) => setDateFilter({...dateFilter, start: e.target.value})} />
                    <span className="text-xs text-slate-400 font-medium">to</span>
                    <input type="date" className="text-xs border-none bg-transparent focus:ring-0 text-slate-600 w-[85px] font-medium cursor-pointer" value={dateFilter.end} onChange={(e) => setDateFilter({...dateFilter, end: e.target.value})} />
                    {(dateFilter.start || dateFilter.end) && (
                        <button onClick={() => setDateFilter({ type: 'all', start: '', end: '' })} className="ml-1 text-slate-400 hover:text-red-500 transition-colors p-0.5">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>

                {/* Work Plan Filter */}
                <div className="flex items-center gap-3">
                    {planWorkstreams.map(workstream => {
                        const colors = getWorkstreamColorClasses(workstream);

                        return (
                            <div key={workstream.key} className={`flex items-center gap-2 p-1.5 rounded-lg border h-9 flex-shrink-0 ${colors.filter}`}>
                                <CalendarDays className={`w-3.5 h-3.5 ${colors.text}`} />
                                <span className={`text-xs font-bold ${colors.text}`}>{workstream.shortLabel || workstream.label}:</span>
                                <select
                                    className="text-xs border-none bg-transparent focus:ring-0 text-slate-600 cursor-pointer font-medium max-w-[110px]"
                                    value={planFilters[workstream.key] || 'all'}
                                    onChange={(e) => setPlanFilters(prev => ({ ...prev, [workstream.key]: e.target.value }))}
                                >
                                    <option value="all">All</option>
                                    <option value="Unscheduled">Unscheduled</option>
                                    {planningMonths.map(month => (
                                        <option key={month} value={month}>
                                            {new Date(`${month}-01`).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })}
                                        </option>
                                    ))}
                                </select>
                                {(planFilters[workstream.key] || 'all') !== 'all' && (
                                    <button onClick={() => setPlanFilters(prev => ({ ...prev, [workstream.key]: 'all' }))} className={`ml-1 ${colors.text}`}>
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Work Status Filter - Simplified Compact Version */}
                <div className="inline-flex items-center gap-0 bg-slate-50 px-1 py-0.5 rounded-lg border border-slate-200 h-9 flex-shrink-0">
                    <Activity className="w-3 h-3 text-red-600 flex-shrink-0" />
                    {statusWorkstreams.map(workstream => (
                        <select
                            key={workstream.key}
                            className="text-xs border-none bg-transparent focus:ring-0 text-slate-600 cursor-pointer font-medium flex-shrink-0 px-0.5 w-[70px]"
                            value={workStatusFilter[workstream.key] || 'all'}
                            onChange={(e) => setWorkStatusFilter({ ...workStatusFilter, [workstream.key]: e.target.value })}
                        >
                            <option value="all">{workstream.shortLabel || workstream.label}</option>
                            {(workflows[workstream.key] || []).map(status => <option key={status} value={status}>{status}</option>)}
                        </select>
                    ))}
                    
                    {hasActiveWorkStatusFilters && (
                        <button onClick={() => setWorkStatusFilter(buildWorkstreamState(config, 'all', workstream => Boolean(workstream.statusField)))} className="text-slate-400 hover:text-red-600 transition-colors p-0.5 flex-shrink-0">
                            <X className="w-3 h-3" />
                        </button>
                    )}
                </div>

                {/* RIGHT ALIGNED GROUP: Search, Notes, Staff, Clear */}
                <div className="flex-1 flex gap-2 justify-end min-w-0 h-9 items-center">
                    
                    {/* Search */}
                    <div className="relative min-w-[120px] flex-1 h-full">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full h-full pl-8 pr-7 py-1 bg-slate-50 border border-slate-200 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white text-slate-900" />
                        {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"><XCircle className="w-3.5 h-3.5" /></button>}
                    </div>

                    {/* Notes Filter - NEW */}
                    <div className="relative h-full flex-shrink-0">
                        <select value={filterNotes} onChange={(e) => setFilterNotes(e.target.value)} className="appearance-none h-full pl-8 pr-2 py-1 bg-slate-50 border border-slate-200 rounded-md text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer w-[110px]">
                            <option value="all">All Notes</option>
                            <option value="has_notes">Has Notes</option>
                        </select>
                        <FileText className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    </div>

                    {/* Staff Filter */}
                    <div className="relative h-full flex-shrink-0">
                        <select value={filterStaff} onChange={(e) => setFilterStaff(e.target.value)} className="appearance-none h-full pl-3 pr-8 py-1 bg-slate-50 border border-slate-200 rounded-md text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer max-w-[150px]">
                            <option value="all">All Staff</option>
                            {staffList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <Filter className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    </div>

                    {/* Clear All Filters */}
                    <button onClick={handleClearFilters} className="h-9 w-9 flex items-center justify-center bg-slate-100 hover:bg-red-50 text-slate-500 hover:text-red-600 border border-slate-200 rounded-md transition-colors flex-shrink-0" title="Clear All Filters">
                        <FilterX className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Bulk Action Bar */}
            {selectedClients.size > 0 && (
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-6 animate-in slide-in-from-bottom-5">
                    <span className="text-sm font-semibold">{selectedClients.size} selected</span>
                    <div className="h-5 w-px bg-white/20"></div>
                    
                    <div className="flex items-center gap-3">
                        <button onClick={() => setShowPrintModal(true)} className="flex flex-col items-center gap-0.5 text-xs text-slate-300 hover:text-white"><Printer className="w-4 h-4" /> Print</button>
                        <button onClick={() => setShowDownloadModal(true)} className="flex flex-col items-center gap-0.5 text-xs text-slate-300 hover:text-white"><Download className="w-4 h-4" /> Download</button>
                        <button onClick={handleOpenEmailModal} className="flex flex-col items-center gap-0.5 text-xs text-slate-300 hover:text-white"><Send className="w-4 h-4" /> Email</button>
                        <button onClick={() => setShowContactsTable(true)} className="flex flex-col items-center gap-0.5 text-xs text-slate-300 hover:text-white"><User className="w-4 h-4" /> Contacts</button>
                        <button onClick={() => setShowNotesTable(true)} className="flex flex-col items-center gap-0.5 text-xs text-slate-300 hover:text-white"><FileText className="w-4 h-4" /> Notes</button>
                        <button onClick={() => setShowBulkAssignModal(true)} className="flex flex-col items-center gap-0.5 text-xs text-slate-300 hover:text-white"><Users className="w-4 h-4" /> Assign</button>
                        <button onClick={() => setShowBulkStatusModal(true)} className="flex flex-col items-center gap-0.5 text-xs text-slate-300 hover:text-white"><CheckSquare className="w-4 h-4" /> Status</button>
                        {supportsBulkServiceEdit && (
                            <button onClick={() => setShowBulkServicesModal(true)} className="flex flex-col items-center gap-0.5 text-xs text-slate-300 hover:text-white"><FileSpreadsheet className="w-4 h-4" /> Services</button>
                        )}
                        
                        {/* Move Dropdown */}
                        {viewMode === 'planning' && (
                            <div className="relative">
                                <button onClick={() => setShowMoveDropdown(!showMoveDropdown)} className="flex flex-col items-center gap-0.5 text-xs text-slate-300 hover:text-white"><ArrowRight className="w-4 h-4" /> Move</button>
                                {showMoveDropdown && (
                                    <div className="absolute bottom-full mb-2 right-0 bg-white text-slate-900 rounded-lg shadow-lg z-50 min-w-[140px]">
                                        <div className="max-h-60 overflow-y-auto">
                                            {[...Array(24)].map((_, i) => {
                                                const now = new Date();
                                                const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
                                                const yyyy = d.getFullYear();
                                                const mm = String(d.getMonth() + 1).padStart(2, '0');
                                                const monthValue = `${yyyy}-${mm}`;
                                                const monthDisplay = d.toLocaleDateString('en-GB', { year: 'numeric', month: 'long' });
                                                return (
                                                    <button
                                                        key={monthValue}
                                                        onClick={() => {
                                                            handleBulkMove(monthValue);
                                                            setShowMoveDropdown(false);
                                                        }}
                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 transition-colors border-b border-slate-100 last:border-b-0"
                                                    >
                                                        {monthDisplay}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                {isLoading ? (
                    <div className="flex-1 flex items-center justify-center p-8">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-slate-600 font-medium">Loading {entity.plural.toLowerCase()}...</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {viewMode === 'list' ? (
                            <ClientTableView 
                                data={paginatedClients} 
                                config={config}
                                columns={config.columns}
                                onUpdate={handleUpdate}
                                staffList={staffList}
                                workflows={workflows}
                                selectedIds={selectedClients}
                                onSelect={(id) => { const n = new Set(selectedClients); if(n.has(id)) n.delete(id); else n.add(id); setSelectedClients(n); }}
                                onSelectAll={() => setSelectedClients(selectedClients.size === paginatedClients.length ? new Set() : new Set(paginatedClients.map(c => c.id)))}
                                onRowClick={setSelectedClientForModal}
                                isBulkEditMode={isBulkEditMode}
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                            />
                        ) : (
                            <ClientKanbanView 
                                data={filteredClients} 
                                config={config}
                                plannerType={plannerType}
                                onMoveMonth={handleMoveMonth}
                                onBulkMove={handleBulkMove}
                                selectedClients={selectedClients}
                                onToggleSelection={(id) => {
                                    const n = new Set(selectedClients);
                                    if (n.has(id)) n.delete(id);
                            else n.add(id);
                            setSelectedClients(n);
                        }}
                        onClientClick={setSelectedClientForModal}
                        staffList={staffList}
                        workflows={workflows}
                        onSaveNotes={handleSaveNotes}
                        onUpdate={handleUpdate}
                        onOpenEmail={(recipients) => {
                            setEmailRecipients(recipients);
                            setShowEmailModal(true);
                        }}
                    />
                )}
                    </>
                )}

                {/* Pagination Footer */}
                {viewMode === 'list' && !isLoading && (
                    <div className="bg-white border-t border-slate-200 p-3 flex items-center justify-between text-xs text-slate-600 flex-none">
                        <div>
                            Showing {filteredClients.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredClients.length)} of {filteredClients.length} records
                        </div>
                        <div className="flex items-center gap-2">
                            <select 
                                value={itemsPerPage} 
                                onChange={(e) => { setItemsPerPage(e.target.value === 'All' ? 'All' : parseInt(e.target.value)); setCurrentPage(1); }}
                                className="border border-slate-300 rounded px-2 py-1 text-xs bg-white cursor-pointer"
                            >
                                <option value="10">10 per page</option>
                                <option value="25">25 per page</option>
                                <option value="50">50 per page</option>
                                <option value="100">100 per page</option>
                                <option value="All">All</option>
                            </select>
                            
                            <div className="flex items-center gap-1">
                                <button 
                                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                    disabled={currentPage === 1}
                                    className="p-1 hover:bg-slate-100 disabled:text-slate-300 disabled:hover:bg-white rounded transition-colors"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <span className="px-2 font-medium">Page {currentPage} of {totalPages || 1}</span>
                                <button 
                                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                    disabled={currentPage === totalPages || totalPages === 0}
                                    className="p-1 hover:bg-slate-100 disabled:text-slate-300 disabled:hover:bg-white rounded transition-colors"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            {selectedClientForModal && (
                <ClientDetailModal 
                    client={selectedClientForModal} 
                    config={config}
                    selectedCount={isBulkEditMode ? selectedClients.size : 0}
                    onClose={() => setSelectedClientForModal(null)} 
                    onSave={handleSaveDetails}
                    onConnectHmrc={handleConnectHmrc}
                    isConnectingHmrc={isConnectingHmrc}
                    onRequestAuth={handleRequestAuthorisation}
                    onCheckInviteStatus={handleCheckInvitationStatus}
                    onSandboxAccept={handleSandboxAccept}
                    onOpenInviteLink={handleOpenInviteLink}
                    isRequestingAuth={isRequestingAuth}
                    isCheckingInvite={isCheckingInvite}
                    hmrcEnvironment={hmrcEnvStatus?.hmrcEnvironment || 'unknown'}
                />
            )}
            <HmrcSyncHistoryModal isOpen={showHmrcSyncHistory} onClose={() => setShowHmrcSyncHistory(false)} />

            {showDownloadModal && (
                <DownloadModal 
                    selectedClients={selectedClients.size > 0 ? Array.from(selectedClients).map(id => clients.find(c => c.id === id)).filter(Boolean) : clients}
                    staffList={staffList}
                    config={config}
                    onClose={() => setShowDownloadModal(false)}
                />
            )}

            {showPrintModal && (
                <PrintModal 
                    selectedClients={selectedClients.size > 0 ? Array.from(selectedClients).map(id => clients.find(c => c.id === id)).filter(Boolean) : clients}
                    staffList={staffList}
                    config={config}
                    onClose={() => setShowPrintModal(false)}
                />
            )}

            {showEmailModal && (
                <EmailComposeModal 
                    recipients={emailRecipients}
                    onClose={() => setShowEmailModal(false)}
                    onSend={handleSendEmail}
                    contextClients={selectedClientData}
                />
            )}

            {showContactsTable && (
                <ContactsListModal 
                    selectedClients={selectedClients.size > 0 ? Array.from(selectedClients).map(id => clients.find(c => c.id === id)).filter(Boolean) : clients}
                    onClose={() => setShowContactsTable(false)}
                    onUpdateClient={handleSaveDetails}
                />
            )}

            {showNotesTable && (
                <NotesListModal 
                    selectedClients={selectedClients.size > 0 ? Array.from(selectedClients).map(id => clients.find(c => c.id === id)).filter(Boolean) : clients}
                    onClose={() => setShowNotesTable(false)}
                    onUpdateClient={handleSaveDetails}
                />
            )}

            {showAddClientModal && !config.enrollMode && (
                <AddCompanyModal
                    onClose={() => setShowAddClientModal(false)}
                    onSave={handleAddClient}
                    config={config}
                />
            )}

            {showAddClientModal && config.enrollMode && (
                <EnrolClientModal
                    onClose={() => setShowAddClientModal(false)}
                    targetTable={config.enrollTargetTable || config.tableName}
                    sourceTypes={config.enrollSourceTypes || ['company', 'charity', 'individual']}
                    moduleLabel={config.label || 'Service'}
                    onEnrol={async (clients) => {
                        await handleEnrolClients(clients);
                        setShowAddClientModal(false);
                    }}
                />
            )}

            {showBulkAssignModal && (
                <BulkAssignModal
                    selectedClients={selectedClients.size > 0 ? Array.from(selectedClients).map(id => clients.find(c => c.id === id)).filter(Boolean) : []}
                    staffList={staffList}
                    config={config}
                    onAssign={handleBulkAssign}
                    onClose={() => setShowBulkAssignModal(false)}
                />
            )}

            {showBulkStatusModal && (
                <BulkStatusModal
                    selectedClients={selectedClients.size > 0 ? Array.from(selectedClients).map(id => clients.find(c => c.id === id)).filter(Boolean) : []}
                    config={config}
                    onUpdateStatus={handleBulkStatusUpdate}
                    onClose={() => setShowBulkStatusModal(false)}
                />
            )}

            {showBulkServicesModal && supportsBulkServiceEdit && (
                <BulkServicesModal
                    selectedClients={selectedClients.size > 0 ? Array.from(selectedClients).map(id => clients.find(c => c.id === id)).filter(Boolean) : []}
                    config={config}
                    onUpdateServices={handleBulkServiceUpdate}
                    onClose={() => setShowBulkServicesModal(false)}
                />
            )}
        </div>
    );
};

export default GenericClientModule;